import { ActionDirectionType, DirectionEnum } from '../types/enums';
import { mapService } from '../services/MapService';
import { dungeonService } from '../services/DungeonService';

export interface SpawnCellOptions {
  /** Allow cells occupied by an entity — item drops may land on monster/player
   *  cells, monster spawns may not. */
  allowOccupied?: boolean;
}

/**
 * Shared placement validity for spawns and drops: a ground tile with no
 * location/POI on it. Monster spawns additionally require an empty cell; item
 * drops pass `allowOccupied` to tolerate monster/player cells.
 */
export const isSpawnableCell = (
  x: number,
  y: number,
  options: SpawnCellOptions = {},
): boolean => {
  if (mapService.getTileAt(x, y)?.type !== 'ground') {
    return false;
  }
  if (dungeonService.locationService.isLocationAt(x, y)) {
    return false;
  }
  if (!options.allowOccupied && dungeonService.getEntityAt(x, y)) {
    return false;
  }
  return true;
};

/**
 * Resolve where a dropped item should land: the requested cell if valid, else
 * the nearest valid cell (Chebyshev rings). Items tolerate occupied cells.
 * `exclude` rejects otherwise-valid cells (e.g. the footprint of a pending
 * dynamic placement). Returns `null` if no valid cell is found nearby.
 */
export const findDropCell = (
  x: number,
  y: number,
  exclude?: (cx: number, cy: number) => boolean,
): { x: number; y: number } | null => {
  const ok = (cx: number, cy: number): boolean =>
    isSpawnableCell(cx, cy, { allowOccupied: true }) && !exclude?.(cx, cy);
  if (ok(x, y)) {
    return { x, y };
  }
  const maxRing = 5;
  for (let r = 1; r <= maxRing; r++) {
    for (let dx = -r; dx <= r; dx++) {
      for (let dy = -r; dy <= r; dy++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) {
          continue;
        }
        if (ok(x + dx, y + dy)) {
          return { x: x + dx, y: y + dy };
        }
      }
    }
  }
  return null;
};

export const getDirectionFromPositions = (
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
): DirectionEnum => {
  const dx = toX - fromX;
  const dy = toY - fromY;

  const angle = Math.atan2(dy, dx);

  // Game coordinate system: Y increases toward TOP, decreases toward BOTTOM.
  // angle = 0: RIGHT, π/2: TOP, π: LEFT, -π/2: BOTTOM.
  const directionIndex = Math.round(((angle + Math.PI) / (Math.PI / 4)) % 8);

  const directions = [
    DirectionEnum.LEFT, // index 0, angle ≈ π
    DirectionEnum['BOTTOM-LEFT'], // index 1, angle ≈ -3π/4
    DirectionEnum.BOTTOM, // index 2, angle ≈ -π/2
    DirectionEnum['BOTTOM-RIGHT'], // index 3, angle ≈ -π/4
    DirectionEnum.RIGHT, // index 4, angle ≈ 0
    DirectionEnum['TOP-RIGHT'], // index 5, angle ≈ π/4
    DirectionEnum.TOP, // index 6, angle ≈ π/2
    DirectionEnum['TOP-LEFT'], // index 7, angle ≈ 3π/4
  ];

  return directions[directionIndex];
};

export const getTargetPosition = (
  currentX: number,
  currentY: number,
  direction: DirectionEnum,
): {
  x: number;
  y: number;
} => {
  let targetX = currentX;
  let targetY = currentY;

  switch (direction) {
    case DirectionEnum.TOP:
      targetY += 1;
      break;
    case DirectionEnum.BOTTOM:
      targetY -= 1;
      break;
    case DirectionEnum.LEFT:
      targetX -= 1;
      break;
    case DirectionEnum.RIGHT:
      targetX += 1;
      break;
    case DirectionEnum['TOP-LEFT']:
      targetX -= 1;
      targetY += 1;
      break;
    case DirectionEnum['TOP-RIGHT']:
      targetX += 1;
      targetY += 1;
      break;
    case DirectionEnum['BOTTOM-LEFT']:
      targetX -= 1;
      targetY -= 1;
      break;
    case DirectionEnum['BOTTOM-RIGHT']:
      targetX += 1;
      targetY -= 1;
      break;
  }

  return { x: targetX, y: targetY };
};

export const isTileBlocking = (
  x: number,
  y: number,
  checkWaterOnly = false,
): boolean => {
  const tile = mapService.getTileAt(x, y);
  if (!tile) {
    return true; // off-map = blocking
  }

  if (checkWaterOnly) {
    return false; // for diagonals, only walls block
  }

  return tile.type === 'wall' || tile.type === 'water';
};

export interface CornerReachOptions {
  range?: number;
  directionType?: ActionDirectionType;
  lineOfSight?: boolean;
  bypassDiagonalMiss?: boolean;
}

/**
 * Whether an action's PRIMARY cell (its anchor: circle center, line/cone first
 * cell, single target) can be reached from `from`, honoring the wall-corner
 * rule. Only the direction of the first step (`Math.sign`) matters. A diagonal
 * primary cell behind a wall corner is unreachable unless the spell allows the
 * cut: ranged (range > 1) AND not LIMITED_ALIGNED AND not LOS-required, or the
 * explicit `bypassDiagonalMiss` flag. Single source of truth shared by the cast
 * pipeline (SpellService) and the monster AI (AIService).
 */
export const canReachThroughCorner = (
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  opts: CornerReachOptions,
): boolean => {
  const dx = toX - fromX;
  const dy = toY - fromY;
  if (dx === 0 || dy === 0) {
    return true;
  }
  if (opts.bypassDiagonalMiss) {
    return true;
  }
  const range = opts.range ?? 1;
  const allowsCornerCut =
    range > 1 &&
    opts.directionType !== ActionDirectionType.LIMITED_ALIGNED &&
    opts.lineOfSight !== true;
  if (allowsCornerCut) {
    return true;
  }
  return mapService.isDiagonalValid(
    fromX,
    fromY,
    fromX + Math.sign(dx),
    fromY + Math.sign(dy),
  );
};

export const getSwipeDirections = (
  direction: DirectionEnum,
): DirectionEnum[] => {
  const swipeMap: Record<DirectionEnum, DirectionEnum[]> = {
    [DirectionEnum.TOP]: [
      DirectionEnum.TOP,
      DirectionEnum['TOP-LEFT'],
      DirectionEnum['TOP-RIGHT'],
    ],
    [DirectionEnum['TOP-RIGHT']]: [
      DirectionEnum['TOP-RIGHT'],
      DirectionEnum.TOP,
      DirectionEnum.RIGHT,
    ],
    [DirectionEnum.RIGHT]: [
      DirectionEnum.RIGHT,
      DirectionEnum['TOP-RIGHT'],
      DirectionEnum['BOTTOM-RIGHT'],
    ],
    [DirectionEnum['BOTTOM-RIGHT']]: [
      DirectionEnum['BOTTOM-RIGHT'],
      DirectionEnum.RIGHT,
      DirectionEnum.BOTTOM,
    ],
    [DirectionEnum.BOTTOM]: [
      DirectionEnum.BOTTOM,
      DirectionEnum['BOTTOM-RIGHT'],
      DirectionEnum['BOTTOM-LEFT'],
    ],
    [DirectionEnum['BOTTOM-LEFT']]: [
      DirectionEnum['BOTTOM-LEFT'],
      DirectionEnum.BOTTOM,
      DirectionEnum.LEFT,
    ],
    [DirectionEnum.LEFT]: [
      DirectionEnum.LEFT,
      DirectionEnum['BOTTOM-LEFT'],
      DirectionEnum['TOP-LEFT'],
    ],
    [DirectionEnum['TOP-LEFT']]: [
      DirectionEnum['TOP-LEFT'],
      DirectionEnum.LEFT,
      DirectionEnum.TOP,
    ],
  };

  return swipeMap[direction];
};

/**
 * The cone only works with LIMITED_ALIGNED directions (TOP, BOTTOM, LEFT,
 * RIGHT).
 */
export function getConeCells(
  currentX: number,
  currentY: number,
  direction: DirectionEnum,
  range: number,
  ordered: true,
): {
  orderedCells: { x: number; y: number }[][];
  allCells: { x: number; y: number }[];
};
export function getConeCells(
  currentX: number,
  currentY: number,
  direction: DirectionEnum,
  range: number,
  ordered?: false,
): { x: number; y: number }[];
export function getConeCells(
  currentX: number,
  currentY: number,
  direction: DirectionEnum,
  range: number,
  ordered?: boolean,
):
  | { x: number; y: number }[]
  | {
      orderedCells: { x: number; y: number }[][];
      allCells: { x: number; y: number }[];
    } {
  let mainDx = 0;
  let mainDy = 0;

  switch (direction) {
    case DirectionEnum.TOP:
      mainDx = 0;
      mainDy = 1;
      break;
    case DirectionEnum.BOTTOM:
      mainDx = 0;
      mainDy = -1;
      break;
    case DirectionEnum.LEFT:
      mainDx = -1;
      mainDy = 0;
      break;
    case DirectionEnum.RIGHT:
      mainDx = 1;
      mainDy = 0;
      break;
    default:
      // Cone only supports the 4 cardinal directions; a diagonal aim hits no
      // cells. Must still honor the `ordered: true` shape contract, else the
      // caller's `const { allCells } = ...` is undefined (crash on spread).
      return ordered ? { orderedCells: [], allCells: [] } : [];
  }

  if (ordered) {
    const cellsByRange: { x: number; y: number }[][] = [];
    const allCells: { x: number; y: number }[] = [];

    const baseCell = { x: currentX, y: currentY };
    cellsByRange.push([baseCell]);
    allCells.push(baseCell);

    for (let i = 1; i <= range; i++) {
      const rangeCells: { x: number; y: number }[] = [];

      const mainX = currentX + mainDx * i;
      const mainY = currentY + mainDy * i;
      const mainCell = { x: mainX, y: mainY };
      rangeCells.push(mainCell);
      allCells.push(mainCell);

      for (let j = 1; j <= i; j++) {
        let diag1X, diag1Y, diag2X, diag2Y;

        if (direction === DirectionEnum.TOP) {
          diag1X = currentX - j;
          diag1Y = currentY + i;
          diag2X = currentX + j;
          diag2Y = currentY + i;
        } else if (direction === DirectionEnum.BOTTOM) {
          diag1X = currentX - j;
          diag1Y = currentY - i;
          diag2X = currentX + j;
          diag2Y = currentY - i;
        } else if (direction === DirectionEnum.LEFT) {
          diag1X = currentX - i;
          diag1Y = currentY - j;
          diag2X = currentX - i;
          diag2Y = currentY + j;
        } else {
          diag1X = currentX + i;
          diag1Y = currentY - j;
          diag2X = currentX + i;
          diag2Y = currentY + j;
        }

        const diag1Cell = { x: diag1X, y: diag1Y };
        const diag2Cell = { x: diag2X, y: diag2Y };
        rangeCells.push(diag1Cell);
        rangeCells.push(diag2Cell);
        allCells.push(diag1Cell);
        allCells.push(diag2Cell);
      }

      cellsByRange.push(rangeCells);
    }

    return { orderedCells: cellsByRange, allCells };
  } else {
    const cells: { x: number; y: number }[] = [];

    cells.push({ x: currentX, y: currentY });

    for (let i = 1; i <= range; i++) {
      const mainX = currentX + mainDx * i;
      const mainY = currentY + mainDy * i;
      cells.push({ x: mainX, y: mainY });

      for (let j = 1; j <= i; j++) {
        let diag1X, diag1Y, diag2X, diag2Y;

        if (direction === DirectionEnum.TOP) {
          diag1X = currentX - j;
          diag1Y = currentY + i;
          diag2X = currentX + j;
          diag2Y = currentY + i;
        } else if (direction === DirectionEnum.BOTTOM) {
          diag1X = currentX - j;
          diag1Y = currentY - i;
          diag2X = currentX + j;
          diag2Y = currentY - i;
        } else if (direction === DirectionEnum.LEFT) {
          diag1X = currentX - i;
          diag1Y = currentY - j;
          diag2X = currentX - i;
          diag2Y = currentY + j;
        } else {
          diag1X = currentX + i;
          diag1Y = currentY - j;
          diag2X = currentX + i;
          diag2Y = currentY + j;
        }

        cells.push({ x: diag1X, y: diag1Y });
        cells.push({ x: diag2X, y: diag2Y });
      }
    }

    return cells;
  }
}
